import type { FormValues } from "@/widgets/classic-form/form";
import { toExactOnly } from "@/entities/data-model";
import type { NameFieldGroup } from "@/entities/form-metadata";
import { NameAssertion } from "@/entities/form-metadata";

/**
 * A collection form can have a second, independent `groupType: "name"` field group for a
 * named non-Self party on the record (e.g. an Executor/Informant, `relation: "Other1"`),
 * distinct from the primary Self name group for the search subject. See TT-3954.
 */
const isNonSelfRelation = (fieldGroup: NameFieldGroup): boolean => {
    const relation = fieldGroup.fields[0]?.relation;
    return !!relation && relation !== "Self";
};

/**
 * Populates the primary Self name group — the search subject's own name. Writes the fixed
 * `name` dataModel slot and carries the compound name-exactness shape (phonetic/similar/
 * initials/soundex flags per assertion), matching legacy's dedicated name-search behavior.
 * See `populateNonSelfNameFields` for a named non-Self party, which has neither.
 */
const populateSelfNameFields = ({
    fieldGroup,
    dataModel,
    formValuesNameData,
    matchAllTermsExactly,
    isEditForm,
}: {
    fieldGroup: NameFieldGroup;
    dataModel: FormValues;
    formValuesNameData: FormValues["name"];
    matchAllTermsExactly: boolean;
    isEditForm?: boolean;
}) => {
    const { name } = dataModel;
    const { isFirstNameExactnessEnabled, isSurnameExactnessEnabled, isFirstNameRequired, isSurnameRequired } =
        fieldGroup.fields.reduce(
            (acc, field) => {
                const {
                    assertion,
                    features: { showExactness, required },
                } = field;
                if (assertion === NameAssertion.GivenName) {
                    acc.isFirstNameExactnessEnabled = showExactness ?? true;
                    acc.isFirstNameRequired = !!required;
                } else if (assertion === NameAssertion.Surname) {
                    acc.isSurnameExactnessEnabled = showExactness ?? true;
                    acc.isSurnameRequired = !!required;
                }
                return acc;
            },
            {
                isFirstNameExactnessEnabled: true,
                isSurnameExactnessEnabled: true,
                isFirstNameRequired: false,
                isSurnameRequired: false,
            },
        );
    const { givenName, givenNameExactness, secondSurname, surname, surnameExactness } = name;
    // @ts-ignore FIXME
    const newData: FormValues["name"] = {
        givenName,
        secondSurname,
        surname,
    };
    if (isFirstNameExactnessEnabled) {
        // A name field that is required for search must be an exact match, so seed its exactness
        // to pure "Exact" (all fuzzy flags cleared) regardless of MAT / edit-form state or any
        // stored preference. This is the baseline the locked NameExactness control renders and
        // the query serializes — matching legacy behavior. See TT-3965.
        // Otherwise: preserve user preference isExact when MAT is off or on edit form; force true
        // when MAT is on for new forms.
        newData.givenNameExactness = isFirstNameRequired
            ? toExactOnly(givenNameExactness)
            : {
                  ...givenNameExactness,
                  isExact: isEditForm ? givenNameExactness.isExact : matchAllTermsExactly || givenNameExactness.isExact,
              };
    }
    if (isSurnameExactnessEnabled) {
        newData.surnameExactness = isSurnameRequired
            ? toExactOnly(surnameExactness)
            : {
                  ...surnameExactness,
                  isExact: isEditForm ? surnameExactness.isExact : matchAllTermsExactly || surnameExactness.isExact,
              };
    }
    Object.assign(formValuesNameData, newData);
};

/**
 * Populates a non-Self name group — a named party on the record other than the search subject
 * (e.g. an Executor/Informant, `relation: "Other1"`). There is no dataModel slot for a second
 * named person, so — mirroring how `otherGroup.ts` keys generic fields by
 * `${relation}${eventName}${assertion}` — its fields are populated into the open `fields` map
 * instead, keyed `${relation}${assertion}` (no eventName segment, matching the
 * `f-{relation}-{assertion}` URL pattern). Carries a single `isExact` flag per field, not the
 * compound name-exactness shape `populateSelfNameFields` uses.
 */
const populateNonSelfNameFields = ({
    fieldGroup,
    dataModel,
    formValuesFieldsData,
    matchAllTermsExactly,
    isEditForm,
}: {
    fieldGroup: NameFieldGroup;
    dataModel: FormValues;
    formValuesFieldsData: FormValues["fields"];
    matchAllTermsExactly: boolean;
    isEditForm?: boolean;
}) => {
    const { fields: fieldsFromDataModel } = dataModel;

    fieldGroup.fields.forEach((field) => {
        const { assertion, relation, features } = field;
        const fieldAccessor = `${relation}${assertion}`;
        const isExactnessEnabled = features.showExactness ?? true;

        const isExactEnabledValue = isEditForm
            ? (fieldsFromDataModel[fieldAccessor]?.isExact ?? false)
            : matchAllTermsExactly
              ? true
              : (fieldsFromDataModel[fieldAccessor]?.isExact ?? false);

        formValuesFieldsData[fieldAccessor] = {
            value: fieldsFromDataModel[fieldAccessor]?.value ?? "",
            relation: relation ?? "",
            eventName: "",
            assertion,
            isExact: isExactnessEnabled ? isExactEnabledValue : false,
        };
    });
};

export const populateNameFields = ({
    fieldGroup,
    dataModel,
    formValuesNameData,
    formValuesFieldsData,
    matchAllTermsExactly,
    isEditForm,
}: {
    fieldGroup: NameFieldGroup;
    dataModel: FormValues;
    formValuesNameData: FormValues["name"];
    formValuesFieldsData: FormValues["fields"];
    matchAllTermsExactly: boolean;
    isEditForm?: boolean;
}) => {
    if (isNonSelfRelation(fieldGroup)) {
        populateNonSelfNameFields({ fieldGroup, dataModel, formValuesFieldsData, matchAllTermsExactly, isEditForm });
        return;
    }
    populateSelfNameFields({ fieldGroup, dataModel, formValuesNameData, matchAllTermsExactly, isEditForm });
};
